import { concat, isEqual, uniqWith } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Button } from 'antd';
import type { MenuProps } from 'antd';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AntdIcon, { DownOutlined } from '@ant-design/icons';
import { useGetDimensionsList } from '@api/common/queryHooks';
import { useGetBlockEventsName, usePresetList } from '@api/emp/queryHooks';
import { useBlockEventSinglePanelStore } from '@components/block-event-single/store';
import BlockAutoFillButton from '@components/common/AutofillButton/BlockAutoFillButton';
import { useDataTransformPanelStore } from '@components/data-transform-panel/store';
import { useDimensionPanelStore } from '@components/dimensions/store';
import { CAN_BE_MAPPED_METADATA_DIMENSION_TYPE } from '@components/mapping-panel/constants/constant';
import {
  IActiveIndices,
  IItemActiveIndices,
  IMappingRules,
  IMetadataItemActiveIndices,
  useMappingPanelStore,
} from '@components/mapping-panel/store';
import { EMappingRuleType } from '@components/mapping-panel/typed';
import { getEmptyRuleGroup } from '@components/mapping-panel/utils/initializeUtils';
import MappingRulesSvg from '@icons/mapping-panel/mapping-rules-icon.svg';
import { blockedCanMapWhiteList, renameTitleDimensionMap } from '@src/common/constant/emp';
import {
  AutofillStatus,
  EDimensionType,
  ERenamePreset,
  ERenameTitle,
  mappedEventDimensionType,
} from '@src/common/constant/enum';
import { ApplyFilterAsEvent, ITransformRule } from '@src/common/types/empTypes';
import { useCustomDimensionPanelStore } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/store';
import { EMetricTypes, EStringOp } from '@src/emp-3.0/types';
import commonSelector from '@store/common/selectors';

import { MappingRulesButton } from './styled';
import { useCreatePermissions } from '@hooks/usePermissions';
import { transformStateToRules } from '@components/mapping-panel/utils/handleRulesUtils';
import { useApplyEventDraft } from '@src/hooks/useNewWorkflow';
import { useMetricBuilderStore } from '@src/emp-3.0/components/Metrics/IndividualMetric/store';

interface IFilterMap {
  [key: string]: any[];
}

export const AutofillButton = ({ children }: { children?: React.ReactElement }) => {
  const location = useLocation();
  const path = location.pathname;
  const hasCreate = useCreatePermissions();

  const [mapAfStatus, setMapAfStatus] = useState<AutofillStatus>(AutofillStatus.CANNOT_AUTOFILL);
  const [renameAfStatus, setRenameAfStatus] = useState<AutofillStatus>(
    AutofillStatus.CANNOT_AUTOFILL,
  );
  const [clicked, setClicked] = useState<boolean>(false);
  const { appliedFilters } = useSelector(commonSelector);
  // const [setInitialEventDraft, setCompleteEventDraft, setErrorEventsDraft] = useFlowStore(
  //   (state) => [state.setInitialEventDraft, state.setCompleteEventDraft, state.setErrorEventsDraft],
  // );

  const [
    mappingRules,
    activeIndices,
    setMappingRules,
    setMappingPanelOpen,
    setActiveIndices,
    mappingPanelOpen,
  ] = useMappingPanelStore((state) => [
    state.mappingRules,
    state.activeIndices,
    state.setMappingRules,
    state.setMappingPanelOpen,
    state.setActiveIndices,
    state.mappingPanelOpen,
  ]);
  const [
    transformPanelOpen,
    transformRule,
    transformTitle,
    setTransformPanelOpen,
    setTransformRule,
    setTransformTargetDimension,
    setTransformTitle,
  ] = useDataTransformPanelStore((state) => [
    state.transformPanelOpen,
    state.transformRule,
    state.transformTitle,
    state.setTransformPanelOpen,
    state.setTransformRule,
    state.setTransformTargetDimension,
    state.setTransformTitle,
  ]);
  const [blockEventSingleOpen] = useBlockEventSinglePanelStore((state) => [
    state.blockEventSingleOpen,
  ]);
  const setCurPresetName = useDimensionPanelStore((state) => state.setCurPresetName);
  const { customDimensionPanelOpen } = useCustomDimensionPanelStore((state) => state);

  const { data: blockedEvents } = useGetBlockEventsName();
  const { data: dimensionList } = useGetDimensionsList();
  const { data: presetList } = usePresetList();
  const eventFilters = useRef<IFilterMap>();
  const metadataData = useRef<any>();
  const transformData = useRef<any>();
  const canMapAutofill = useRef<boolean>(false);
  const canMetadataAutofill = useRef<boolean>(false);
  const canTransformAutofill = useRef<boolean>(false);

  const isWorkFlowPage = useMemo(() => path.includes(`/workflow/`), [path]);
  const isFlowPage = useMemo(() => path.includes(`/workflow/flow`), [path]);
  const { metricType } = useMetricBuilderStore();
  const applyEventDraft = useApplyEventDraft(isFlowPage);

  const metadataDimensionList = useMemo(() => {
    return (dimensionList?.data ?? []).filter((dimension) => {
      return (
        dimension?.can_be_mapped &&
        CAN_BE_MAPPED_METADATA_DIMENSION_TYPE.includes(dimension?.dimension_type)
      );
    });
  }, [dimensionList?.data]);

  const transformDimensionList = useMemo(() => {
    return (dimensionList?.data ?? []).filter((dimension) => {
      return dimension?.dimension_type === EDimensionType.EVENT_ATTRIBUTE;
    });
  }, [dimensionList?.data]);

  const attributeList = useMemo(() => {
    const res = {};
    (presetList?.data ?? []).forEach((preset, key) => {
      res[preset?.preset_name ?? key] = (preset?.dimension_list ?? [])
        .filter((dimension) => dimension.dimension_type === EDimensionType.EVENT_ATTRIBUTE)
        .map((dimension) => dimension.dimension_name);
    });
    return res;
  }, [presetList?.data]);

  const generateMetadataFromFilter = (rules, list) => {
    return (rules ?? [])
      .map((andRules) => {
        return andRules
          .map((orRule) => {
            const canBeMapped = list.find(
              (canBeMappedDimension) => canBeMappedDimension?.dimension_name === orRule?.field,
            );
            if (canBeMapped) {
              return {
                key: canBeMapped?.dimension_name,
                op: orRule.op ?? EStringOp.EQ,
                value: [orRule.value],
              };
            }
            return;
          })
          .filter(Boolean);
      })
      .filter((subArray) => subArray.length > 0);
  };

  const handleMap = (rules) => {
    const resMap: IFilterMap = {
      [mappedEventDimensionType.all_event_name]: [],
      [mappedEventDimensionType.merged_event_tag_key]: [],
      [mappedEventDimensionType.merged_event_tag_value]: [],
    };
    let canMapAutofillFlag = true;
    for (const ruleGroup of rules) {
      const andMap: IFilterMap = {
        [mappedEventDimensionType.all_event_name]: [],
        [mappedEventDimensionType.custom_event_name]: [],
        [mappedEventDimensionType.conviva_event_name]: [],
        [mappedEventDimensionType.merged_event_tag_key]: [],
        [mappedEventDimensionType.merged_event_tag_value]: [],
      };
      for (const rule of ruleGroup) {
        if (
          rule.field === mappedEventDimensionType.all_event_name ||
          rule.field === mappedEventDimensionType.custom_event_name ||
          rule.field === mappedEventDimensionType.conviva_event_name ||
          rule.field === mappedEventDimensionType.merged_event_tag_key ||
          rule.field === mappedEventDimensionType.merged_event_tag_value
        ) {
          // make sure that
          // 1. the logic operators between event name/tag key/tag value are AND
          // 2. the logic operators between tag value are OR
          if (
            andMap[mappedEventDimensionType.all_event_name].length > 0 ||
            andMap[mappedEventDimensionType.custom_event_name].length > 0 ||
            andMap[mappedEventDimensionType.conviva_event_name].length > 0 ||
            andMap[mappedEventDimensionType.merged_event_tag_key].length > 0 ||
            (rule.field !== mappedEventDimensionType.merged_event_tag_value &&
              andMap[mappedEventDimensionType.merged_event_tag_value].length > 0) ||
            (rule.field === mappedEventDimensionType.merged_event_tag_value &&
              resMap[mappedEventDimensionType.merged_event_tag_value].length > 0)
          ) {
            canMapAutofillFlag = false;
            break;
          }
          andMap[rule.field].push(rule);
        }
      }
      if (!canMapAutofillFlag) {
        break;
      }
      resMap[mappedEventDimensionType.all_event_name].push(
        ...andMap[mappedEventDimensionType.all_event_name],
        ...andMap[mappedEventDimensionType.custom_event_name],
        ...andMap[mappedEventDimensionType.conviva_event_name],
      );
      resMap[mappedEventDimensionType.merged_event_tag_key].push(
        ...andMap[mappedEventDimensionType.merged_event_tag_key],
      );
      resMap[mappedEventDimensionType.merged_event_tag_value].push(
        ...andMap[mappedEventDimensionType.merged_event_tag_value],
      );
    }
    // make sure that
    // 1. we only have one event name
    // 1. we have less than one tag key
    if (
      resMap[mappedEventDimensionType.all_event_name].length !== 1 ||
      resMap[mappedEventDimensionType.merged_event_tag_key].length > 1
    ) {
      canMapAutofillFlag = false;
    }
    // If event name is blocked, can not autofill
    const filteredBlockedEvents = (blockedEvents ?? []).filter(
      (event) => !blockedCanMapWhiteList.includes(event),
    );
    if (filteredBlockedEvents.includes(resMap[mappedEventDimensionType.all_event_name][0]?.value)) {
      canMapAutofillFlag = false;
    }
    if (canMapAutofillFlag) {
      eventFilters.current = resMap;
    }
    return canMapAutofillFlag;
  };

  const handleMetadata = (rules) => {
    const res = generateMetadataFromFilter(rules, metadataDimensionList);
    if (res.length > 0) {
      metadataData.current = res;
      return true;
    }
    return false;
  };

  const handleTransform = (rules) => {
    let resRules = generateMetadataFromFilter(rules, transformDimensionList);
    resRules = resRules
      .map((andGroup) => {
        return andGroup
          .map((rule) => {
            if (rule.op === EStringOp.EQ || rule.op === EStringOp.CONTAINS) {
              return rule;
            }
          })
          .filter(Boolean);
      })
      .filter((subArray) => subArray.length > 0);
    // only keep rules which have the same key with the first rule in each And group
    resRules = resRules.map((andGroup) => {
      const key = andGroup[0].key;
      return andGroup.filter((rule) => rule.key === key);
    });
    const pageRes: any[] = [];
    const screenRes: any[] = [];
    resRules.forEach((andGroup) => {
      const key = andGroup[0].key;
      if (attributeList[ERenamePreset.PAGE]?.includes(key)) {
        pageRes.push(andGroup);
      }
      if (attributeList[ERenamePreset.SCREEN]?.includes(key)) {
        screenRes.push(andGroup);
      }
    });
    const res: any = { rules: [], title: ERenameTitle.PAGE };
    if (transformPanelOpen && transformTitle === ERenameTitle.PAGE) {
      res.rules = pageRes;
    } else if (transformPanelOpen && transformTitle === ERenameTitle.SCREEN) {
      res.rules = screenRes;
      res.title = ERenameTitle.SCREEN;
    } else {
      // when Data Transform Panel not open and both page and screen rules can be auto-filled
      // preferred auto-filling page rules
      if (pageRes.length > 0) {
        res.rules = pageRes;
      } else {
        res.rules = screenRes;
        res.title = ERenameTitle.SCREEN;
      }
    }
    if (res.rules.length > 0) {
      transformData.current = res;
      return true;
    }
    return false;
  };

  const handleFiltersChange = (rules) => {
    canMapAutofill.current = handleMap(rules);
    const selectedMappingRules = mappingRules;
    canMetadataAutofill.current =
      (selectedMappingRules.some((ruleGroup) => !!ruleGroup?.eventName) ||
        canMapAutofill.current) &&
      handleMetadata(rules);
    canTransformAutofill.current = handleTransform(rules);
    canMapAutofill.current || canMetadataAutofill.current
      ? setMapAfStatus(AutofillStatus.CAN_AUTOFILL)
      : setMapAfStatus(AutofillStatus.CANNOT_AUTOFILL);
    canTransformAutofill.current
      ? setRenameAfStatus(AutofillStatus.CAN_AUTOFILL)
      : setRenameAfStatus(AutofillStatus.CANNOT_AUTOFILL);
  };

  useEffect(() => {
    handleFiltersChange(appliedFilters[0].rule);
  }, [
    appliedFilters,
    blockedEvents,
    transformTitle,
    transformPanelOpen,
    mappingRules,
    transformDimensionList,
    attributeList,
  ]);

  useEffect(() => {
    if (!mappingPanelOpen) {
      handleFiltersChange(appliedFilters[0].rule);
      setClicked(false);
    }
  }, [mappingPanelOpen]);

  useEffect(() => {
    if (!transformPanelOpen) {
      handleFiltersChange(appliedFilters[0].rule);
      setClicked(false);
    }
  }, [transformPanelOpen]);

  useEffect(() => {
    setClicked(false);
  }, [appliedFilters]);

  const generateMappingRules = () => {
    const eventName = eventFilters.current?.[mappedEventDimensionType.all_event_name][0];
    const tagKey = eventFilters.current?.[mappedEventDimensionType.merged_event_tag_key][0];
    const tagValues = eventFilters.current?.[mappedEventDimensionType.merged_event_tag_value];
    const res = [...mappingRules];
    if (canMapAutofill.current) {
      let orIndex = 0;
      for (const ruleGroup of res) {
        if (eventName && ruleGroup.eventName === eventName.value) {
          break;
        }
        orIndex++;
      }
      if (orIndex === res.length) {
        const lastRuleGroup = res[res.length - 1];
        if (
          !lastRuleGroup.eventName &&
          lastRuleGroup.rules.length === 1 &&
          lastRuleGroup.rules[0].length === 1 &&
          !lastRuleGroup.rules[0][0].tagKey &&
          !lastRuleGroup.rules[0][0].tagValue.value
        ) {
          orIndex--;
        }
        if (eventName) {
          const newRuleGroup: IMappingRules = getEmptyRuleGroup();
          newRuleGroup.eventName = eventName.value;
          res[orIndex] = newRuleGroup;
        }
      }
      const andGroupLength = res[orIndex].rules.length;
      const orGroupLength = res[orIndex].rules[andGroupLength - 1].length;
      const lastRuleItem = res[orIndex].rules[andGroupLength - 1][orGroupLength - 1];
      const activeIndices: IActiveIndices = {
        ruleType: EMappingRuleType.NORMAL,
        orIndex,
        eventName: true,
      };
      if (!lastRuleItem.tagKey && !lastRuleItem.tagValue.value) {
        res[orIndex].rules[andGroupLength - 1].pop();
      }
      const subOrIndexBase = res[orIndex].rules[andGroupLength - 1].length;
      const newTagValues = tagValues?.length ? tagValues : [{ op: EStringOp.EQ, value: undefined }];
      const itemActiveIndices: IItemActiveIndices[] = [];
      newTagValues.forEach((tagValue, index) => {
        res[orIndex].rules[andGroupLength - 1].push({
          tagKey: tagKey?.value,
          tagValue: {
            operator: tagValue.op,
            value: tagValue.value,
          },
        });
        itemActiveIndices.push({
          subAndIndex: andGroupLength - 1,
          subOrIndex: subOrIndexBase + index,
          tagKey: !!tagKey?.value,
          tagValue: !!tagValue?.value,
        });
      });
      activeIndices.ruleItems = itemActiveIndices;
    }
    if (canMetadataAutofill.current) {
      const metadataItemActiveIndices: IMetadataItemActiveIndices[] = [];
      metadataData.current.forEach((andMetadata, andIndex) => {
        andMetadata.forEach((orMetadata, orIndex) => {
          metadataItemActiveIndices.push({
            subAndIndex: andIndex,
            subOrIndex: orIndex,
          });
        });
      });

      activeIndices.metadataItems = metadataItemActiveIndices;

      if (metadataData.current) {
        res?.forEach((ruleGroup) => {
          if (ruleGroup?.eventName) {
            if (ruleGroup.metadata) {
              const mergedMetadata = concat(ruleGroup.metadata, metadataData.current);
              ruleGroup.metadata = uniqWith(mergedMetadata, isEqual);
            } else {
              ruleGroup.metadata = metadataData.current;
            }
          }
        });
      }
    }
    return { mappingRules: res, activeIndices };
  };

  const generateTransformRules = () => {
    return transformData.current.rules.map((andGroup) => ({
      source_dimension: andGroup[0].key,
      rule: [andGroup],
    }));
  };

  const handleMapAfClick = () => {
    if (mapAfStatus === AutofillStatus.CANNOT_AUTOFILL || clicked) {
      return;
    }
    setMappingPanelOpen(true);
    const res = generateMappingRules();
    setMappingRules([...res.mappingRules]);
    setActiveIndices(res.activeIndices);
    setClicked(true);
  };

  const isLastRuleEmpty = (rule: ITransformRule[] = []) => {
    return !(
      rule[rule.length - 1]?.source_dimension &&
      rule[rule.length - 1]?.rule?.some((group) => group.some((item) => item.value.length > 0))
    );
  };

  const handleRenameAfClick = () => {
    if (renameAfStatus === AutofillStatus.CANNOT_AUTOFILL || clicked) {
      return;
    }
    if (!transformPanelOpen) {
      setTransformPanelOpen(true);
      setTransformTargetDimension(renameTitleDimensionMap[transformData.current.title]);
      setTransformTitle(transformData.current.title);
      if (transformData.current.title === ERenameTitle.PAGE) {
        setCurPresetName(ERenamePreset.PAGE);
      } else if (transformData.current.title === ERenameTitle.SCREEN) {
        setCurPresetName(ERenamePreset.SCREEN);
      }
    }
    const res = generateTransformRules();
    if (isLastRuleEmpty(transformRule)) {
      const newTransformRule = [...transformRule];
      newTransformRule.pop();
      setTransformRule([...newTransformRule, ...res]);
    } else {
      setTransformRule([...transformRule, ...res]);
    }
    setClicked(true);
  };

  const mapAfBtn = (
    <MappingRulesButton onClick={handleMapAfClick} disabled={clicked}>
      <AntdIcon component={MappingRulesSvg} style={{ fontSize: '20px' }} />
      <span>{mappingPanelOpen ? 'Add To Rule' : 'Map This'}</span>
    </MappingRulesButton>
  );

  const renameAfBtn = (
    <MappingRulesButton onClick={handleRenameAfClick} disabled={clicked}>
      <AntdIcon component={MappingRulesSvg} style={{ fontSize: '20px' }} />
      <span>{transformPanelOpen ? 'Add To Rule' : 'Rename This'}</span>
    </MappingRulesButton>
  );
  let items: MenuProps['items'] = [
    {
      label: `Initial Event`,
      key: '0',
      onClick: () => {
        applyAs('initial');
      },
    },
    {
      label: 'Complete Event',
      key: '1',
      onClick: () => {
        applyAs('complete');
      },
    },
    {
      label: 'New Error Event',
      key: '2',
      onClick: () => {
        applyAs('error');
      },
    },
  ];
  if (!isFlowPage) {
    items = [
      {
        label: `Initial Event`,
        key: '0',
        onClick: () => {
          applyAs('initial');
        },
      },
      {
        label: 'Complete Event',
        key: '1',
        onClick: () => {
          applyAs('complete');
        },
      },
    ];
    if (metricType === EMetricTypes.SINGLE_EVENT) {
      items = [
        {
          label: `Event`,
          key: '0',
          onClick: () => {
            applyAs('initial');
          },
        },
      ];
    } else if (metricType === EMetricTypes.RATIO_METRIC) {
      items = [
        {
          label: 'Numerator Event',
          key: '0',
          onClick: () => {
            applyAs('numerator');
          },
        },
        {
          label: 'Denominator Event',
          key: '1',
          onClick: () => {
            applyAs('denominator');
          },
        },
      ];
    }
  }

  const applyAs = (type: ApplyFilterAsEvent) => {
    const res = generateMappingRules();
    const rules = transformStateToRules([...res.mappingRules]);
    // HP-11193 keep name empty
    const draft = {
      event_category: 'Custom',
      event_name: '',
      rules: [[...rules]],
    };
    applyEventDraft(draft, type);
  };
  const applyAsBtn =
    mapAfStatus !== AutofillStatus.CANNOT_AUTOFILL ? (
      <Dropdown menu={{ items }} trigger={['hover']}>
        <Button type="primary">
          Apply as...&nbsp;&nbsp;
          <DownOutlined />
        </Button>
      </Dropdown>
    ) : null;

  if (isWorkFlowPage) {
    return applyAsBtn;
  }

  if (!hasCreate) return null;

  if (children) {
    if (mapAfStatus !== AutofillStatus.CANNOT_AUTOFILL) {
      return React.cloneElement(children, {
        disabled: clicked,
        onClick: () => {
          if (children.props.children === 'Apply as...') {
            const res = generateMappingRules();
            children.props.onClick([...res.mappingRules]);
            return;
          }
          const res = generateMappingRules();
          setMappingRules([...res.mappingRules]); // keep add last status
          setClicked(true);
          if (children.props.onClick) {
            children.props.onClick([...res.mappingRules]);
          }
        },
      });
    }
  }

  if (blockEventSingleOpen) {
    return <BlockAutoFillButton />;
  }

  if (!customDimensionPanelOpen) {
    // preferred to displaying map auto-fill btn
    if (mapAfStatus !== AutofillStatus.CANNOT_AUTOFILL && !transformPanelOpen) {
      return mapAfBtn;
    } else if (renameAfStatus !== AutofillStatus.CANNOT_AUTOFILL && !mappingPanelOpen) {
      return renameAfBtn;
    }
  }
  return null;
};
